/**
 * Voice Recorder Hook
 *
 * State machine for browser audio recording and Groq Whisper transcription.
 * Flow: idle → recording → transcribing → idle
 *
 * DRY: Uses existing apiClient for auth headers + tenant ID injection.
 *
 * @module useVoiceRecorder
 */

'use client';

import { useState, useRef, useCallback, useEffect } from 'react';

import apiClient from '@/lib/api-client';

// ============================================================================
// Constants
// ============================================================================

/** Maximum recording duration in seconds */
const MAX_RECORDING_DURATION_SECONDS = 120;

/** Minimum recording duration in ms — below this, audio is too short for Whisper and causes hallucinations (e.g., "Altyazı M.K." in Turkish) */
const MIN_RECORDING_DURATION_MS = 1000;

/**
 * Error keys — namespace-agnostic. Each consumer maps these to its own
 * i18n bundle via the `VoiceLabels.errors` map (see `VoiceRecordButton`).
 */
const VOICE_ERROR_KEYS = {
  NOT_SUPPORTED: 'notSupported',
  PERMISSION_DENIED: 'permissionDenied',
  TRANSCRIPTION_FAILED: 'transcriptionFailed',
  NO_SPEECH_DETECTED: 'noSpeechDetected',
} as const;

/** Union type of all possible voice error keys. */
export type VoiceErrorKey = (typeof VOICE_ERROR_KEYS)[keyof typeof VOICE_ERROR_KEYS];

/** Preferred MIME types in priority order */
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

// ============================================================================
// Types
// ============================================================================

type RecorderState = 'idle' | 'recording' | 'transcribing';

interface TranscriptionResponse {
  text: string;
  language: string;
  durationSeconds: number;
  processingTimeMs: number;
  isEmpty: boolean;
}

export interface UseVoiceRecorderOptions {
  /** Language hint for Whisper */
  language?: 'en' | 'tr';
  /** Callback when transcription completes with text */
  onTranscription?: (text: string) => void;
  /** Callback on error */
  onError?: (error: string) => void;
}

export interface UseVoiceRecorderReturn {
  /** Current state of the recorder */
  state: RecorderState;
  /** Whether currently recording */
  isRecording: boolean;
  /** Whether currently transcribing */
  isTranscribing: boolean;
  /** Recording duration in seconds */
  duration: number;
  /** Error key (maps to chatbot.voice.* i18n key) */
  error: VoiceErrorKey | null;
  /** Start recording */
  startRecording: () => Promise<void>;
  /** Stop recording and begin transcription */
  stopRecording: () => void;
  /** Cancel recording without transcription */
  cancelRecording: () => void;
  /** Whether the browser supports MediaRecorder */
  isSupported: boolean;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for browser audio recording and voice transcription.
 *
 * @param options - Configuration options
 * @returns Voice recorder state and controls
 */
export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}): UseVoiceRecorderReturn {
  const { language = 'en', onTranscription, onError } = options;

  const [state, setState] = useState<RecorderState>('idle');
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<VoiceErrorKey | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  // Check browser support after mount (avoids SSR hydration mismatch)
  useEffect(() => {
    setIsSupported(
      typeof navigator !== 'undefined' &&
        !!navigator.mediaDevices?.getUserMedia &&
        typeof MediaRecorder !== 'undefined'
    );
  }, []);

  /**
   * Get supported MIME type for MediaRecorder
   */
  const getSupportedMimeType = useCallback((): string => {
    for (const type of PREFERRED_MIME_TYPES) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return ''; // Let browser choose default
  }, []);

  /**
   * Clean up media stream and timer
   */
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  /**
   * Send audio blob to backend for transcription
   */
  const transcribe = useCallback(
    async (blob: Blob) => {
      setState('transcribing');
      setError(null);

      try {
        const extension = blob.type.includes('ogg') ? 'ogg' : 'webm';
        const formData = new FormData();
        formData.append('audio', blob, `recording.${extension}`);
        formData.append('language', language);

        const response = await apiClient.post<TranscriptionResponse>(
          '/ai-chatbot/voice/transcribe',
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30_000,
          }
        );

        const { text, isEmpty } = response.data;

        if (isEmpty || !text.trim()) {
          setError(VOICE_ERROR_KEYS.NO_SPEECH_DETECTED);
          onError?.(VOICE_ERROR_KEYS.NO_SPEECH_DETECTED);
        } else {
          onTranscription?.(text);
        }
      } catch {
        setError(VOICE_ERROR_KEYS.TRANSCRIPTION_FAILED);
        onError?.(VOICE_ERROR_KEYS.TRANSCRIPTION_FAILED);
      } finally {
        setState('idle');
      }
    },
    [language, onTranscription, onError]
  );

  /**
   * Start recording audio from microphone
   */
  const startRecording = useCallback(async () => {
    // Always reset error first so re-clicking triggers React state change
    setError(null);

    if (!isSupported) {
      // Use microtask to ensure null→error transition triggers useEffect
      queueMicrotask(() => {
        setError(VOICE_ERROR_KEYS.NOT_SUPPORTED);
        onError?.(VOICE_ERROR_KEYS.NOT_SUPPORTED);
      });
      return;
    }

    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const chunks = chunksRef.current;
        const recordingDurationMs = Date.now() - startTimeRef.current;

        if (chunks.length > 0 && recordingDurationMs >= MIN_RECORDING_DURATION_MS) {
          const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
          void transcribe(blob);
        } else {
          // Too short or no data — skip transcription to prevent Whisper hallucinations
          setState('idle');
        }
        // Stop tracks after recorder stops
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start();
      startTimeRef.current = Date.now();
      setState('recording');
      setDuration(0);

      // Throttled tabs can fire two ticks in the same wall-clock second;
      // guard so consumers don't re-render on identical values.
      let lastEmittedSecond = 0;
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        if (elapsed !== lastEmittedSecond) {
          lastEmittedSecond = elapsed;
          setDuration(elapsed);
        }

        if (elapsed >= MAX_RECORDING_DURATION_SECONDS) {
          recorder.stop();
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }, 1000);
    } catch (err) {
      cleanup();
      const errorKey =
        (err as DOMException)?.name === 'NotAllowedError'
          ? VOICE_ERROR_KEYS.PERMISSION_DENIED
          : VOICE_ERROR_KEYS.TRANSCRIPTION_FAILED;
      setError(errorKey);
      onError?.(errorKey);
      setState('idle');
    }
  }, [isSupported, getSupportedMimeType, transcribe, cleanup, onError]);

  /**
   * Stop recording and trigger transcription
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Cancel recording without transcribing
   */
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      // Remove the onstop handler to prevent transcription
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    cleanup();
    setState('idle');
    setDuration(0);
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    state,
    isRecording: state === 'recording',
    isTranscribing: state === 'transcribing',
    duration,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
    isSupported,
  };
}
